import { useEffect, type FC, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Badge,
  useTheme,
  styled,
  Button,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { MAGIC_TYPE_ENUM } from "../../utils/constants";
import { MainMagicShortcutType } from "../main-page/Shortcuts";

const APP_ID = "wx31a857d3d6e9c63e";
const REDIRECT_URI = encodeURIComponent(
  `https://www.lingqi.tech//aigc/api/wx/login/callback`
); // /aigc/wx/callback
const SCOPE = "snsapi_login";
// const STATE = window.location.href; // UUID, 用于防止 CSRF 攻击

// 定义旋转动画
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const warning = keyframes`
  0% {  box-shadow: 0 0 2px 2px #FFA500; }
  50% { box-shadow: 0 0 2px 4px #FF4D00; }
  100% { box-shadow: 0 0 2px 2px #FFA500; }
`;

// 定义“呼吸”动画（渐变放大缩小）
const breathe = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

type TopBarProps = {
  userInfo: any;
  onLogoClick: () => void;
  onAvatarClick: () => void;
  magicType: MAGIC_TYPE_ENUM | null;
  magicShortcut: MainMagicShortcutType | null;
};

export const TopBar: FC<TopBarProps> = ({
  userInfo,
  onLogoClick,
  onAvatarClick,
  magicType,
  magicShortcut,
}) => {
  const theme = useTheme();

  const [stateWX, setStateWX] = useState("");
  const [hover, setHover] = useState(false);
  const [scrolling, setScrolling] = useState(false);

  useEffect(() => {
    const address = window.location.href;
    const idx = address.indexOf("?");
    if (idx > 0) {
      const rootURI = address.substring(0, idx - 1);
      setStateWX(encodeURIComponent(rootURI));
    } else {
      setStateWX(encodeURIComponent(address));
    }

    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolling(true);
      } else {
        setScrolling(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 32px",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        gap: "16px",
        backgroundColor: scrolling ? "rgba(179, 157, 219, 0.9)" : "transparent",
        boxShadow: scrolling
          ? "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)"
          : "none",
        borderBottom: scrolling
          ? `1px solid ${theme.palette.grey[500]}`
          : "none",
      }}
      id="top-bar"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          animation: `${breathe} 2.5s ease-in-out infinite`,
          cursor: "pointer",
          ":hover": {
            color:
              magicType && magicShortcut === null
                ? theme.palette.primary.dark
                : theme.palette.secondary.dark,
          },
        }}
        onClick={onLogoClick}
      >
        <img src="logo192.png" alt="logo" width="32px" height="32px" />
        <Typography variant="h5" fontWeight={500} noWrap>
          灵起AI
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {userInfo !== null && (
          <Box onClick={onAvatarClick}>
            <Badge
              badgeContent={hover ? String(userInfo.point) : null}
              color={
                magicType && magicShortcut === null ? "primary" : "secondary"
              }
              overlap="circular"
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
            >
              <Avatar
                alt={userInfo.nickname}
                src={userInfo.avatar}
                sx={{
                  border: `1px solid ${theme.palette.grey[500]}`,
                  boxShadow: `0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)`,
                  animation:
                    userInfo.point < 10
                      ? `${warning} 3s linear infinite`
                      : `${rotate} 30s linear infinite`,
                  ":hover": {
                    cursor: "pointer",
                  },
                }}
              />
            </Badge>
          </Box>
        )}
        {userInfo === null && (
          <CoolButton
            onClick={() => {
              window.location.href = `https://open.weixin.qq.com/connect/qrconnect?appid=${APP_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=${SCOPE}&state=${stateWX}#wechat_redirect`;
            }}
            sx={{
              animation: scrolling
                ? `${breathe} 2.5s ease-in-out infinite`
                : "none",
              border: scrolling ? `1px solid white` : "none",
            }}
          >
            登录
          </CoolButton>
        )}
      </Box>
    </Box>
  );
};

export const CoolButton = styled(Button)(({ theme }) => ({
  position: "relative",
  padding: "4px 32px",
  borderRadius: "30px",
  fontWeight: 800,
  fontSize: "18px",
  textTransform: "none",
  color: "#fff",
  background: "linear-gradient(45deg, #2196F3, #9C27B0, #E91E63)",
  backgroundSize: "300% 300%",
  transition: "all 0.3s ease",
  boxShadow: "0 5px 15px rgba(0,0,0,0.3)",

  "&:hover": {
    backgroundPosition: "right center",
    transform: "scale(1.05)",
    boxShadow: "0 8px 25px rgba(156, 39, 176, 0.6)",
  },

  "&:active": {
    transform: "scale(0.95)",
    boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
  },
}));
